import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, Globe, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export default function Header() {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1A1A1A]/95 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
            <img
              src="/assets/logo-dark.png"
              alt="CREE8 Solutions"
              className="h-10 w-auto"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace("/", "") || "home"}`}
                className={`text-sm font-medium transition-colors duration-300 ${
                  isActive(link.to)
                    ? "text-[#E8A400]"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={toggleLanguage}
              data-testid="language-switcher"
              className="flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors duration-300"
            >
              <Globe className="w-4 h-4" />
              {language === "en" ? "ES" : "EN"}
            </button>
            <Link
              to="/contact"
              data-testid="header-cta"
              className="bg-[#E8A400] text-[#1A1A1A] text-sm font-semibold px-6 py-2.5 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm flex items-center gap-2"
            >
              {t.nav.getInTouch}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-4">
            <button
              onClick={toggleLanguage}
              data-testid="language-switcher-mobile"
              className="flex items-center gap-1 text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              <Globe className="w-4 h-4" />
              {language === "en" ? "ES" : "EN"}
            </button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" className="text-white p-1">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-[#1A1A1A] border-l border-white/10 w-[280px] p-0"
              >
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Website navigation links</SheetDescription>
                <nav className="flex flex-col pt-16 px-8" data-testid="mobile-nav">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${link.to.replace("/", "") || "home"}`}
                      className={`py-4 text-lg font-medium border-b border-white/5 transition-colors ${
                        isActive(link.to)
                          ? "text-[#E8A400]"
                          : "text-white/70 hover:text-white"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-cta"
                    className="mt-8 bg-[#E8A400] text-[#1A1A1A] text-center font-semibold px-6 py-3 hover:bg-[#C68C00] transition-colors rounded-sm"
                  >
                    {t.nav.getInTouch}
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
